import { useEffect, useState, useCallback } from "react";
import { X } from "lucide-react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";

import { FormField } from "@/components/ui/FormField";
import { createAppVersion, updateAppVersionStatus } from "@/services/offers";

interface AppVersionModalProps {
    appVersion?: any;
    onClose: () => void;
    onSuccess: () => void;
}

interface AppVersionFormData {
    Version_Code: string;
    Version: string;
    File: string;
}

export default function AddMobileUpdate({
    appVersion,
    onClose,
    onSuccess,
}: AppVersionModalProps) {
    const {
        control,
        handleSubmit,
        reset,
        setError,
        clearErrors,
        formState: { isSubmitting, errors },
    } = useForm<AppVersionFormData>({
        defaultValues: {
            Version_Code: "",
            Version: "",
            File: "",
        },
    });

    const [generalError, setGeneralError] = useState("");

    useEffect(() => {
        if (!appVersion) {
            reset({ Version_Code: "", Version: "", File: "" });
            setGeneralError("");
            return;
        }

        reset({
            Version_Code: String(appVersion.Version_Code ?? ""),
            Version: appVersion.Version ?? "",
            File: appVersion.File ?? "",
        });

        setGeneralError("");
    }, [appVersion, reset]);

    const handleApiErrors = useCallback(
        (response: any) => {
            clearErrors();
            setGeneralError("");

            const data =
                response?.data ||
                response?.errors ||
                response ||
                {};

            if (!data || typeof data !== "object") {
                toast.error("Something went wrong");
                return;
            }

            let hasFieldError = false;

            Object.entries(data).forEach(([key, value]: any) => {
                const message = Array.isArray(value) ? value[0] : value;

                switch (key) {
                    case "Version_Code":
                        setError("Version_Code", {
                            type: "server",
                            message,
                        });
                        hasFieldError = true;
                        break;

                    case "Version":
                        setError("Version", {
                            type: "server",
                            message,
                        });
                        hasFieldError = true;
                        break;

                    case "File":
                        setError("File", {
                            type: "server",
                            message,
                        });
                        // setGeneralError(message);
                        toast.error(message);
                        hasFieldError = true;
                        break;
                }
            });

            if (data.error) {
                const msg = Array.isArray(data.error)
                    ? data.error[0]
                    : data.error;
                setGeneralError(msg);
                toast.error(msg);
            } else if (!hasFieldError) {
                toast.error("Something went wrong");
            }
        },
        [clearErrors, setError]
    );

    const onSubmit = async (values: AppVersionFormData) => {
        try {
            clearErrors();
            setGeneralError("");

            let hasError = false;

            if (!values.Version_Code) {
                setError("Version_Code", {
                    type: "manual",
                    message: "Version Code is required",
                });
                hasError = true;
            }

            if (!values.Version) {
                setError("Version", {
                    type: "manual",
                    message: "Version is required",
                });
                hasError = true;
            }

             if (!values.File) {
                setError("File", {
                    type: "manual",
                    message: "Url is required",
                });
                hasError = true;
            }

            if (hasError) return;

            const payload = {
                Version_Code: values.Version_Code,
                Version: values.Version,
                File: values.File,
            };

            const response = appVersion
                ? await updateAppVersionStatus(appVersion.id, payload)
                : await createAppVersion(payload);

            if (response?.status === 200 || response?.status === 201) {
                toast.success(
                    appVersion
                        ? "App Version updated successfully"
                        : "App Version created successfully"
                );
                onSuccess();
            } else {
                handleApiErrors(response);
            }
        } catch (error) {
            handleApiErrors(error);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] flex flex-col">

                {/* Header */}
                <div className="flex justify-between items-center p-5 bg-gray-800 rounded-t-2xl flex-shrink-0">
                    <h2 className="text-white text-xl font-bold">
                        {appVersion ? "Edit App Version" : "Create App Version"}
                    </h2>
                    <button onClick={onClose} type="button" aria-label="Close modal">
                        <X className="text-white" />
                    </button>
                </div>

                {/* Scrollable body */}
                <div className="overflow-y-auto flex-1">
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <div className="p-6 grid gap-5">

                            {/* Version Code */}
                            <FormField
                                control={control}
                                name="Version_Code"
                                label="Version Code"
                                type="text"
                                required
                                error={errors.Version_Code?.message}
                            />

                            {/* Version */}
                            <FormField
                                control={control}
                                name="Version"
                                label="Version"
                                type="text"
                                required
                                error={errors.Version?.message}
                            />

                            {/* File (text field) */}
                            <FormField
                                control={control}
                                name="File"
                                label="Url"
                                type="text"
                                required
                                error={errors.File?.message}
                            />

                            {/* General / server error */}
                            {generalError && (
                                <p className="text-red-500 text-sm">{generalError}</p>
                            )}
                        </div>

                        {/* Footer buttons */}
                        <div className="p-5 flex gap-3 border-t border-gray-100 flex-shrink-0">
                            <button
                                type="button"
                                onClick={onClose}
                                className="flex-1 bg-gray-200 hover:bg-gray-300 py-3 rounded transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="flex-1 bg-yellow-400 hover:bg-yellow-500 py-3 rounded font-medium transition-colors disabled:opacity-60"
                            >
                                {isSubmitting
                                    ? "Saving..."
                                    : appVersion
                                    ? "Update"
                                    : "Create"}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}